import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, CheckCircle2, AlertTriangle, FileText, ArrowRightLeft } from "lucide-react";

const SECTIONS = [
  ["overview", "Overview of the Identified Need"], ["approach", "Proposed Approach"],
  ["scope", "Scope of Services"], ["schedule", "Schedule / Service Structure"],
  ["deliverables", "Deliverables"], ["qualifications", "Proposed Consultant Qualifications"],
  ["assumptions", "Assumptions & Exclusions"], ["next_steps", "Next Steps & Acceptance"],
];
const STATUS_BADGE = { draft: "badge-draft", in_review: "badge-submitted", approved: "badge-approved" };

export default function ProposalDetail() {
  const { proposalId } = useParams();
  const [p, setP] = useState(null);
  const [sections, setSections] = useState(null);
  const [busy, setBusy] = useState("");
  const [regen, setRegen] = useState(null);
  const [outcome, setOutcome] = useState({ outcome: "none", date: "", note: "" });
  const [showVersions, setShowVersions] = useState(false);
  const [convert, setConvert] = useState(null);

  const load = () => api.get(`/admin/proposals/${proposalId}`).then((r) => {
    setP(r.data);
    setSections({ ...r.data.sections });
    setOutcome(r.data.outcome?.status ? r.data.outcome : { outcome: "none", date: "", note: "" });
  });
  useEffect(() => { load(); }, [proposalId]);

  if (!p || !sections) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="proposal-loading">Loading proposal…</div>;

  const isDraft = p.placeholders.length > 0 || p.status !== "approved";

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/admin/proposals/${proposalId}`, { sections });
      await load();
      toast.success("Saved as a new version — prior versions are preserved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const regenerate = async (section) => {
    setBusy(section);
    try {
      await api.post(`/admin/proposals/${proposalId}/regenerate-section`, { section, extra_instructions: regen?.instructions || "" });
      await load();
      setRegen(null);
      toast.success(`Section "${section}" regenerated. Other sections untouched.`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const setStatus = async (status) => {
    setBusy(status);
    try {
      await api.post(`/admin/proposals/${proposalId}/status`, { status });
      await load();
      toast.success(`Status: ${status.replace("_", " ")}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const saveOutcome = async () => {
    try {
      await api.post(`/admin/proposals/${proposalId}/outcome`, outcome);
      await load();
      toast.success("External outcome recorded. The system did not send anything or obtain a signature.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = (fmt) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/proposals/${proposalId}/export.${fmt}`, "_blank");
  };

  const loadConvertPreview = async () => {
    try {
      const { data } = await api.get(`/admin/proposals/${proposalId}/convert-preview`);
      setConvert({ ...data, start_date: "", end_date: "", planned_days: 0, title: data.title });
    } catch (e) { toast.error(apiError(e)); }
  };

  const doConvert = async () => {
    try {
      await api.post(`/admin/proposals/${proposalId}/convert`, convert);
      await load();
      setConvert(null);
      toast.success("Engagement created from proposal, reusing existing client and school records.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="proposal-detail-page">
      <div className="fade-up">
        <Link to="/proposals" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-proposals-link">
          <ArrowLeft size={14} /> Proposals</Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow">{p.organization_name}</p>
            <h1 className="section-title text-3xl mt-1">{p.title}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={STATUS_BADGE[p.status]} data-testid="proposal-status-badge">{p.status.replace("_", " ")}</span>
            <span className="badge-neutral">v{p.version}</span>
            {isDraft && <span className="badge-draft" data-testid="proposal-draft-badge"><AlertTriangle size={11} /> Draft — unresolved fields</span>}
          </div>
        </div>
      </div>

      {p.placeholders.length > 0 && (
        <div className="notice-privacy" data-testid="proposal-placeholders-panel">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <b>Unresolved fields block approval and final export:</b>
            <ul className="list-disc ml-4 mt-1">
              {p.placeholders.map((ph, i) => <li key={i} className="text-xs">{ph.text} <span className="opacity-70">({ph.section})</span></li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="card p-5 fade-up-1" data-testid="proposal-sources-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-2">Source materials used</h2>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#57534E]">
          <span><b>Consultants:</b> {p.sources?.consultants?.join(", ") || "none"}</span>
          <span><b>Company materials:</b> {p.sources?.materials?.join(", ") || "none"}</span>
          <span><b>Price entries:</b> {p.sources?.price_entries?.join(", ") || "none"}</span>
          <span><b>Client brief:</b> {p.sources?.rfp_used ? "yes (source material only)" : "no"}</span>
        </div>
        <p className="text-[11px] text-[#A8A29E] mt-2">Consultant compensation and internal notes are never included in proposal generation or output.</p>
      </div>

      <div className="space-y-4 fade-up-2" data-testid="proposal-sections">
        {SECTIONS.map(([key, label]) => (
          <div key={key} className="card p-5" data-testid={`proposal-section-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif font-bold text-navy">{label}</h3>
              <button className="btn-ghost !py-1 !px-2 text-xs" data-testid={`regen-${key}`}
                onClick={() => setRegen(regen?.section === key ? null : { section: key, instructions: "" })}>
                <RefreshCw size={12} /> Regenerate</button>
            </div>
            {regen?.section === key && (
              <div className="flex gap-2 mb-2">
                <input className="input !py-1.5 text-xs" placeholder="Guidance for regeneration (optional)" value={regen.instructions}
                  data-testid={`regen-instructions-${key}`} onChange={(e) => setRegen({ ...regen, instructions: e.target.value })} />
                <button className="btn-primary !py-1.5 text-xs" onClick={() => regenerate(key)} disabled={busy === key} data-testid={`regen-confirm-${key}`}>
                  {busy === key ? "Working…" : "Regenerate"}</button>
              </div>
            )}
            <textarea className="input min-h-[110px] text-sm leading-relaxed" value={sections[key] || ""}
              data-testid={`section-text-${key}`}
              onChange={(e) => setSections({ ...sections, [key]: e.target.value })} />
          </div>
        ))}
      </div>

      <div className="card p-5 fade-up-2" data-testid="proposal-pricing-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-3">Itemized pricing (computed by the system, never by AI)</h2>
        <table className="w-full">
          <thead><tr><th className="table-th">Item</th><th className="table-th">Unit</th><th className="table-th">Qty</th>
            <th className="table-th">Unit price</th><th className="table-th">Total</th></tr></thead>
          <tbody>
            {p.pricing_lines.map((l, i) => (
              <tr key={i} data-testid={`pricing-row-${i}`}>
                <td className="table-td font-bold">{l.name}</td>
                <td className="table-td text-xs">{l.billing_unit}</td>
                <td className="table-td">{l.quantity}</td>
                <td className="table-td">{l.unit_price != null ? `${l.currency} ${l.unit_price.toLocaleString()}` : <span className="badge-draft">Needs confirmation</span>}</td>
                <td className="table-td font-bold">{l.line_total != null ? `${l.currency} ${l.line_total.toLocaleString()}` : "—"}</td>
              </tr>
            ))}
            <tr className="bg-sand/60">
              <td className="table-td font-black" colSpan={4}>Total (confirmed lines only)</td>
              <td className="table-td font-black text-navy" data-testid="proposal-total">
                {p.pricing_lines[0]?.currency || "USD"} {p.total?.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card p-5 space-y-4 fade-up-3" data-testid="proposal-actions-card">
        <h2 className="font-serif font-bold text-lg text-navy">Review, export & outcome</h2>
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary" onClick={save} disabled={busy === "save"} data-testid="proposal-save-button">
            {busy === "save" ? "Saving…" : "Save edits (new version)"}</button>
          {p.status === "draft" && <button className="btn-outline" onClick={() => setStatus("in_review")} disabled={!!busy} data-testid="proposal-inreview-button">Mark In Review</button>}
          {p.status === "in_review" && <button className="btn-outline" onClick={() => setStatus("draft")} data-testid="proposal-backdraft-button">Back to Draft</button>}
          {p.status !== "approved" && <button className="btn-navy" onClick={() => setStatus("approved")} disabled={!!busy} data-testid="proposal-approve-button">
            <CheckCircle2 size={14} /> Approve (blocked until placeholders resolved)</button>}
          <button className="btn-outline" onClick={() => download("pdf")} data-testid="export-pdf-button"><Download size={14} /> PDF {isDraft && "(Draft-labeled)"}</button>
          <button className="btn-outline" onClick={() => download("docx")} data-testid="export-docx-button"><Download size={14} /> DOCX {isDraft && "(Draft-labeled)"}</button>
          {!p.engagement_id && <button className="btn-ghost border border-[#E7E1D4]" onClick={loadConvertPreview} data-testid="convert-preview-button">
            <ArrowRightLeft size={14} /> Convert to engagement…</button>}
          {p.engagement_id && <Link to={`/engagements/${p.engagement_id}`} className="btn-ghost border border-[#6EE7B7] text-[#065F46]" data-testid="linked-engagement-link">
            Linked engagement ✓</Link>}
        </div>
        {convert && (
          <div className="agent-panel p-4 space-y-3" data-testid="convert-preview-panel">
            <div className="text-sm font-black text-[#92400E]">Preview — nothing saved yet</div>
            <div className="text-xs text-[#57534E]">Creates an engagement for <b>{convert.organization_name}</b>, reusing schools: {convert.schools_reused.join(", ") || "—"}. No duplicate client records.</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className="label">Title</label>
                <input className="input !py-1.5 text-xs" value={convert.title} data-testid="convert-title" onChange={(e) => setConvert({ ...convert, title: e.target.value })} /></div>
              <div><label className="label">Start date</label>
                <input type="date" className="input !py-1.5 text-xs" value={convert.start_date} data-testid="convert-start" onChange={(e) => setConvert({ ...convert, start_date: e.target.value })} /></div>
              <div><label className="label">End date</label>
                <input type="date" className="input !py-1.5 text-xs" value={convert.end_date} data-testid="convert-end" onChange={(e) => setConvert({ ...convert, end_date: e.target.value })} /></div>
            </div>
            <div className="flex gap-2">
              <button className="btn-primary !py-1.5" onClick={doConvert} data-testid="convert-confirm-button">Create engagement</button>
              <button className="btn-ghost !py-1.5" onClick={() => setConvert(null)}>Cancel</button>
            </div>
          </div>
        )}
        <div className="border-t border-[#F3EFE6] pt-4">
          <div className="label">External outcome (recorded manually — not proof of sending or signature)</div>
          <div className="flex flex-wrap items-end gap-3">
            <select className="input max-w-[160px]" value={outcome.outcome} data-testid="outcome-select"
              onChange={(e) => setOutcome({ ...outcome, outcome: e.target.value })}>
              <option value="none">None recorded</option><option value="sent">Sent</option>
              <option value="accepted">Accepted</option><option value="declined">Declined</option></select>
            <input type="date" className="input max-w-[160px]" value={outcome.date} data-testid="outcome-date"
              onChange={(e) => setOutcome({ ...outcome, date: e.target.value })} />
            <input className="input flex-1 min-w-[180px]" placeholder="Note" value={outcome.note} data-testid="outcome-note"
              onChange={(e) => setOutcome({ ...outcome, note: e.target.value })} />
            <button className="btn-outline !py-1.5" onClick={saveOutcome} data-testid="outcome-save-button">Record outcome</button>
          </div>
        </div>
        <div>
          <button className="btn-ghost !py-1 text-xs" onClick={() => setShowVersions(!showVersions)} data-testid="versions-toggle">
            <FileText size={12} /> Version history ({p.versions.length})</button>
          {showVersions && (
            <ul className="mt-2 space-y-1.5" data-testid="versions-list">
              {[...p.versions].reverse().map((v) => (
                <li key={v.version + v.saved_at} className="text-xs rounded-lg bg-sand px-3 py-2" data-testid={`version-${v.version}`}>
                  <b>v{v.version}</b> · {v.note} · {v.saved_by} · {new Date(v.saved_at).toLocaleString()} ·
                  total {v.total != null ? `$${v.total.toLocaleString()}` : "—"} (snapshot — source changes never rewrite it)
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
